import MainCard from 'components/MainCard';
import ScrollX from 'components/ScrollX';
import {
  GridType,
  Underwriter,
  MerchantCashAdvance,
  Recurrency,
  RecurrencyState,
  Rights,
  SortArgs,
  Syndication,
  useMerchantCashAdvancesPreAggregatedLazyQuery,
  useMerchantCashAdvancesSummaryLazyQuery,
  useMcaDisplayAttributesQuery,
  useNonWorkingDaysQuery,
  useOfferByMcaIdQuery,
  User
} from '../../graphql/types';
import { isUserFunderSyndicator } from '../../utils/mca';
import React, { createContext, SyntheticEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
// AgGrid
import { AgGridReact } from 'ag-grid-react';
import {
  GetRowIdFunc,
  GetRowIdParams,
  GridApi,
  ICellRendererParams,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  SideBarDef,
  ValueFormatterParams
} from 'ag-grid-enterprise';
import {
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  SortChangedEvent,
  GridReadyEvent,
  PaginationChangedEvent,
  RowClickedEvent,
  FilterModel,
  ColDef,
  IRowNode,
} from 'ag-grid-community';
import 'ag-grid-enterprise/styles/ag-grid.css';
import 'ag-grid-enterprise/styles/ag-theme-material.css';
import 'assets/third-party/ag-theme-material-dark.scss';

import {
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  OutlinedInput,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import {
  BankOutlined,
  EditOutlined,
  ExportOutlined,
  FilterOutlined,
  InteractionOutlined,
  FlagOutlined,
  PlusOutlined,
  ProjectOutlined,
  ReloadOutlined,
  SearchOutlined,
  SettingOutlined,
  RiseOutlined,
  CreditCardOutlined,
  DashboardOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import useAuth from '../../hooks/useAuth';
import { useTheme } from '@mui/material/styles';
import AddMerchantCashAdvance from '../../sections/merchants/AddMerchantCashAdvance';
import useConfig from '../../hooks/useConfig';
import { useLocation, useNavigate } from 'react-router-dom';
import McaDetails from '../../sections/merchants/McaDetails';
import { DefaultAgGridTooltip } from '../../components/ag-grid/DefaultAgGridTooltip';
import { AgGridCurrencyCell, FactorCell } from '../../components/ag-grid/AgGridCurrencyCell';
import { getFormattedDateUTC } from '../../utils/getFormattedDate';
import { CurrencyText } from '../../components/input-formatters/CurrencyText';
import { AgGridPercentCell } from '../../components/ag-grid/AgGridPercentCell';
import {
  BootstrapTooltip,
  buildMcaStatusCornerBadge,
  filterMcaStatusValues,
  getStatusLabel,
  getStatusTooltipTitle,
  McaStatusChipCellRenderer
} from '../../sections/merchants/McaStatusChip';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import McaProgressTooltip from '../../sections/merchants/McaProgressTooltip';
import McaListTools from '../../sections/merchants/McaListTools';
import { calculateSyndicationPnlNet } from '../../sections/merchants/SyndicationDetails';
import { getUserProfileFullName } from '../../utils/users';
import IntegrationsImportProgress from '../../sections/integrations/IntegrationsImportProgress';
import AssetLogo from '../../sections/AssetLogo';
import { ProgressBarCell } from '../../components/ag-grid/AgGridProgressBarCell';
import RecurrencyAchDetailsDialog from '../../sections/merchants/RecurrencyAchDetailsDialog';
import InvestorDropDownAutoComplete from '../../components/InvestorDropDownAutoComplete';
import { reportApi } from '../../api/reportApi';
import AgGridCurrencyChipCell from '../../components/ag-grid/AgGridCurrencyChipCell';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import ActionConfirmation from '../../components/mca/ActionConfirmation';
import useAgGridOutsideClick from '../../components/ag-grid/useAgGridOutsideClick';
import StatusChip from '../../components/StatusChip';
import { McaEvent } from '../../sockets/mcaSocketService';
import { useMcaUpdates } from '../../hooks/useMcaUpdates';
import { Logger } from '../../utils/logger';
import ExternalConnectionCompanyLogo from '../../utils/externalConnectionCompanyLogo';
import { applyGridColumnState } from 'utils/agGridUtils';
import UserCellRenderer from '../../components/ag-grid/UserCellRenderer';
import CompaniesWidget from '../../sections/companies/CompaniesWidget';
import SortableIconHeader from 'components/SortableIconHeader';
import McaUnderwriterListWidget from 'sections/merchants/McaUnderwriterListWidget';
import CustomTooltip from 'components/CustomTooltip';

dayjs.extend(utc);

const McaListFilterContext = createContext<{
  filterState: FilterModel | null;
  setFilterState: (filters: FilterModel | null) => void;
}>({
  filterState: null,
  setFilterState: () => { }
});

export const McaListFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [filterState, setFilterState] = useState<FilterModel | null>(null);
  return <McaListFilterContext.Provider value={{ filterState, setFilterState }}>{children}</McaListFilterContext.Provider>;
};

const DEFAULT_HEIGHT = 500;
const ROW_HEIGHT = 47.77;
const ROW_HEIGHT_EXPANDED = 68.4;
const PADDING = 210;
const MAX_HEIGHT = 800;
const HEIGHT_OFFSET = 160;

const perPage = 100;

// AG-Grid SSRM Configuration - Optimized to prevent infinite loops
const CACHE_BLOCK_SIZE = 100;
const CACHE_BLOCK_IN_CACHE = 20; // Increased to keep more data in memory
const MAX_CONCURRENT_REQUESTS = 2; // Increased to allow parallel fetching
const CACHE_OVERFLOW_SIZE = 5; // Increased to allow more overflow
const ROW_BUFFER = 20; // Increased to pre-render more rows
const SERVER_SIDE_INITIAL_ROW_COUNT = 1000;
const BLOCK_LOAD_DEBOUNCE_MILLIS = 100; // Reduced for responsive scrolling

// DIAGNOSTIC: Cache state monitoring
interface CacheStateInfo {
  blockStates: any[];
  infiniteRowCount: number;
  displayedRowCount: number;
  timestamp: number;
  requestKey: string;
}

//

const IconActionBtn = ({ clicked, data, node }: ICellRendererParams & { clicked: (data: any) => {} }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  if (node.isRowPinned()) {
    return null;
  }

  return node.isHovered() ? (
    <>
      {data.currentUserRights.includes(Rights.EditMca) && (
        <Tooltip title="Edit">
          <IconButton
            className="ag-padded-action-button"
            color="primary"
            onClick={(e: any) => {
              e.stopPropagation();
              clicked(data);
            }}
          >
            <EditOutlined />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="View Details">
        <IconButton
          className="ag-padded-action-button"
          color="primary"
          onClick={(e: any) => {
            e.stopPropagation();
            navigate(`/merchants/mca/${data.id}`);
          }}
        >
          <ProjectOutlined twoToneColor={theme.palette.primary.dark} />
        </IconButton>
      </Tooltip>
    </>
  ) : (
    <></>
  );
};

const getPnlNetValue = (data: MerchantCashAdvance, isRowPinned: boolean, currentUser: User | any): number => {
  if (!data) return 0;

  let amount = 0;
  if (isRowPinned) {
    amount = data.pnlNet || 0;
  } else {
    const syndication = data.syndications?.find((s: Syndication) => s.userId === currentUser?.id);
    amount = syndication
      ? calculateSyndicationPnlNet(data, syndication)
      : currentUser?.rights?.includes(Rights.ViewMcaPnl)
        ? data.pnlNet || 0
        : 0;
  }
  return typeof amount === 'number' && !isNaN(amount) ? amount : 0;
};
const PnlNetCell = ({ data, node, useCompactViewStyle = false }: ICellRendererParams & { useCompactViewStyle?: boolean }) => {
  const { user: currentUser } = useAuth();
  const amount = getPnlNetValue(data, node.isRowPinned(), currentUser);
  if (amount === 0) {
    return <CurrencyText value={amount} />;
  }
  return useCompactViewStyle ? (
    <StatusChip label={<CurrencyText value={amount} />} color={amount > 0 ? 'success' : 'error'} size="small" variant="light" />
  ) : (
    <Chip label={<CurrencyText value={amount} />} color={amount > 0 ? 'success' : 'error'} size="small" variant="light" />
  );
};
const getCommissionAmount = (data: any) => {
  if (!data || data.amount == null || data.commissionPercentage == null) return 0;
  const result = (data.amount * data.commissionPercentage) / 100;
  return typeof result === 'number' && !isNaN(result) ? result : 0;
};
const CommissionCell = ({ data, node }: ICellRendererParams) => {
  if (node.isRowPinned()) {
    return <CurrencyText value={data?.commissionAmount || 0} />;
  }
  return <CurrencyText value={getCommissionAmount(data)} />;
};

const logger = new Logger('McaList');

const McaList: React.FC = () => {
  const useQuery = () => {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  };
  const query = useQuery();
  const [statusFilter, setStatusFilter] = useState(() => {
    const statuses = query.getAll('status');
    return statuses.length > 0 ? statuses : [];
  });
  const [fundingDateFilter, setFundingDateFilter] = useState(query.get('fundingDate') || '');
  const [startDateFilter, setStartDateFilter] = useState(query.get('startDate') || '');
  const [funderFilter, setFunderFilter] = useState<string[]>([]);
  const [openDrawerConfirmation, setOpenDrawerConfirmation] = useState<boolean>(false);
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);
  const [rowHeightExpanded, setRowHeightExpanded] = useState<number>(0);
  const handleHeightChange = (newHeight: number) => {
    setRowHeightExpanded(newHeight + ROW_HEIGHT_EXPANDED);
  };
  const { filterState, setFilterState } = useContext(McaListFilterContext);
  const [initialInvestorId, setInitialInvestorId] = useState<string | null>(null);

  const [cacheStates, setCacheStates] = useState<CacheStateInfo[]>([]);
  const [socketUpdatesEnabled, setSocketUpdatesEnabled] = useState<boolean>(true);
  const [expandedMcaId, setExpandedMcaId] = useState<string | null>(null);

  const DetailCellRenderer = (params: ICellRendererParams) => {
    const { data, api } = params;
    const navigate = useNavigate();

    return (
      <div style={{ overflow: 'hidden' }}>
        <McaDetails mca={data} onHeightChange={handleHeightChange} investorId={undefined} rowExpanded={true} />
        <Grid container spacing={2} pl={2} pb={2}>
          {api.getColumn('actions')?.getColDef().cellRendererParams.canViewMcaDetails && (

            <Grid item>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<ProjectOutlined />}
                onClick={(e: any) => {
                  e.stopPropagation();
                  navigate(`/merchants/mca/${data.id}`);
                }}
              >
                View Details
              </Button>
            </Grid>
          )}
          {data?.currentUserRights?.includes(Rights?.EditMca) && (
            <Grid item>
              <Button
                variant="contained"
                startIcon={<EditOutlined />}
                onClick={(e: SyntheticEvent) => {
                  e.stopPropagation();
                  setCashAdvance(data);
                  handleDrawerOpen();
                }}
              >
                Edit
              </Button>
            </Grid>
          )}
        </Grid>
      </div>
    );
  };
  const { data: nonWorkingDaysData } = useNonWorkingDaysQuery({
    variables: {},
    fetchPolicy: 'cache-and-network'
  });

  const { user: currentUser } = useAuth();
  const canViewMcaFunder = currentUser?.rights.includes(Rights.ViewMcaFunder);

  const navigate = useNavigate();
  const userId = currentUser?.id;
  const keyName = `${userId}_MCAS_GRID`;
  const theme = useTheme();
  const { mode } = useConfig();
  const matchDownSM = useMediaQuery(theme.breakpoints.down('sm'));
  const isRowExpandable = currentUser?.rights.includes(Rights.ExpandMcaRow);
  const canCreateMca = currentUser?.rights.includes(Rights.CreateMca);
  const isSuperAdmin = currentUser?.rights.includes(Rights.IsSuperAdmin);

  const { data: offerByMcaData } = useOfferByMcaIdQuery({
    variables: { mcaId: expandedMcaId! },
    fetchPolicy: 'network-only',
    skip: !expandedMcaId || !isSuperAdmin
  });
  const linkedApplication = offerByMcaData?.offerByMcaId?.application;

  const IsCollection = currentUser?.rights.includes(Rights.IsCollection);
  const canViewMcaIntegration = currentUser?.rights.includes(Rights.ViewMcaIntegration);
  const canViewMcaDetails = currentUser?.rights.includes(Rights.ViewMcaDetails);
  const canViewMcaFundingCompany = currentUser?.rights.includes(Rights.ViewMcaFundingCompany);
  const canViewMcaCommission = currentUser?.rights.includes(Rights.ViewMcaCommission);
  const canViewMcaFees = currentUser?.rights.includes(Rights.ViewMcaFees);
  const canViewMcaFundingFees = currentUser?.rights.includes(Rights.ViewMcaFundingFees);
  const canViewMcaBalanceWithFees = currentUser?.rights.includes(Rights.ViewMcaBalanceWithFees);
  const canViewMcaPnl = currentUser?.rights.includes(Rights.ViewMcaPnl);
  const canViewMcaNetWire = currentUser?.rights.includes(Rights.ViewMcaNetWire);
  const canViewMcaBroker = currentUser?.rights.includes(Rights.ViewMcaBroker);
  const canViewMcaISOManager = currentUser?.rights.includes(Rights.ViewMcaIsoManager);
  const canViewMcaLegal = currentUser?.rights.includes(Rights.ViewMcaLegal);

  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [openRecurrencyAchDetails, setOpenRecurrencyAchDetails] = useState<boolean>(false);
  const handleDrawerOpen = () => {
    setOpenDrawer((prevState) => !prevState);
  };
  const handleAdd = useCallback(() => {
    setCashAdvance(undefined);
    setOpenDrawer((prevState) => !prevState);
  }, []);

  const [backdropOpen, setBackdropOpen] = useState<boolean>(false);
  const [reloadTransactions, setReloadTransactions] = useState<boolean>(false);
  const [cashAdvance, setCashAdvance] = useState<MerchantCashAdvance | undefined>();
  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);
  const gridApiRef = useRef<GridApi>();
  const [currentTotalRows, setCurrentTotalRows] = useState<number>(0);
  const [currentPageRows, setCurrentPageRows] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(parseInt(localStorage.getItem(keyName + '_ROWS_PER_PAGE_STATE') || '') || perPage);
  const [searchValue, setSearchValue] = useState('');
  const nonWorkingDays = useMemo(() => nonWorkingDaysData?.nonWorkingDays || [], [nonWorkingDaysData]);
  const { data: mcaDisplayAttributesData } = useMcaDisplayAttributesQuery({ fetchPolicy: 'network-only' });
  const mcaDisplayAttributeRows = useMemo(
    () => mcaDisplayAttributesData?.mcaDisplayAttributes || [],
    [mcaDisplayAttributesData]
  );
  const useScroll = localStorage.getItem(keyName + '_USE_SCROLL') === 'false' ? null : true;
  const useCompactView = localStorage.getItem(keyName + '_USE_COMPACT_VIEW') === 'false' ? null : true;
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const monitorCacheState = useCallback((requestKey: string, context: string) => {
    if (!gridApiRef.current) return;

    try {
      const cacheBlockStates = (gridApiRef.current as any).serverSideRowModel?.rowNodeBlockLoader?.blockCache?.blocks || [];
      const infiniteRowCount = gridApiRef.current.getDisplayedRowCount();
      const displayedRowCount = gridApiRef.current.getDisplayedRowCount();

      const cacheInfo: CacheStateInfo = {
        blockStates: cacheBlockStates.map((block: any) => ({
          id: block.id,
          startRow: block.startRow,
          endRow: block.endRow,
          state: block.state,
          version: block.version
        })),
        infiniteRowCount,
        displayedRowCount,
        timestamp: Date.now(),
        requestKey
      };

      setCacheStates((prev) => [...prev.slice(-10), cacheInfo]); // Keep last 10 states
    } catch (error) {
      console.error('Failed to monitor cache state', error);
    }
  }, []);

  const handleMcaUpdate = useCallback(
    (event: McaEvent) => {
      if (!gridApiRef.current || !socketUpdatesEnabled) return;

      try {
        switch (event.type) {
          case 'created':
            const addResult = gridApiRef.current.applyServerSideTransaction({
              add: [event.mca],
              addIndex: 0
            });

            if (addResult?.add?.length) {
              setCurrentTotalRows((prev) => prev + 1);
              setCurrentPageRows((prev) => prev + 1);
              monitorCacheState(`socket-add-${event.mca.id}`, 'SOCKET_ADD');
            }
            break;

          case 'updated':
            let updateResult = gridApiRef.current.applyServerSideTransaction({
              update: [event.mca]
            });

            if (updateResult?.update?.length) {
              monitorCacheState(`socket-update-${event.mca.id}`, 'SOCKET_UPDATE');
            } else {
              // Fallback to manual update if transaction fails
              gridApiRef.current.forEachNode((node: IRowNode<any>) => {
                if (node.data && node.data.id === event.mca.id) {
                  (node as any).setData(event.mca);
                  gridApiRef.current!.refreshCells({
                    rowNodes: [node],
                    force: true
                  });
                }
              });
            }
            break;

          case 'deleted':
            const deleteResult = gridApiRef.current.applyServerSideTransaction({
              remove: [{ id: event.mca.id }]
            });

            if (deleteResult?.remove?.length) {
              setCurrentTotalRows((prev) => prev - 1);
              setCurrentPageRows((prev) => prev - 1);
              monitorCacheState(`socket-delete-${event.mca.id}`, 'SOCKET_DELETE');
            }
            break;
        }
      } catch (error) {
        logger.error(`Error updating grid from socket event: ${error}`);
        setTimeout(() => {
          if (gridApiRef.current && !gridApiRef.current.isDestroyed()) {
            gridApiRef.current.refreshServerSide();
          }
        }, 1000);
      }
    },
    [socketUpdatesEnabled, monitorCacheState]
  );

  useMcaUpdates(handleMcaUpdate);
  const defaultColDef = useMemo<ColDef>(() => {
    return {
      suppressHeaderMenuButton: true,
      editable: false,
      sortable: true,
      resizable: true,
      pivot: true,
      filter: false,
      columnChooserParams: {
        suppressColumnFilter: true,
        contractColumnSelection: true
      },
      tooltipComponent: DefaultAgGridTooltip
    };
  }, []);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: '#',
        field: 'number',
        headerClass: 'ag-header-cell-limegreen-center',
        toolPanelClass: ['ag-header-cell-limegreen-center'],
        cellClass: 'ag-centered-cell',
        resizable: true
      },
      {
        headerName: 'Status',
        field: 'status',
        valueGetter: (params) =>
          params.data
            ? `${params.data.status}|${(params.data.displayAttributes || []).join(',')}`
            : null,
        filterValueGetter: (params) => params.data?.status || null,
        filter: IsCollection ? false : 'agSetColumnFilter',
        cellRenderer: McaStatusChipCellRenderer,
        cellClass: 'ag-centered-cell',
        headerClass: 'ag-header-cell-limegreen-center',
        toolPanelClass: ['ag-header-cell-limegreen-center'],
        headerComponent: SortableIconHeader,
        headerComponentParams: {
          icon: <SettingOutlined style={{ fontSize: 20 }} />,
          tooltipTitle: 'Status'
        },
        cellRendererParams: (params: ICellRendererParams) => ({
          useCompactViewStyle: !!useCompactView,
          openInNewTab: false,
          nonWorkingDays,
          mcaCornerBadge: buildMcaStatusCornerBadge(params.data || null, mcaDisplayAttributeRows)
        }),
        suppressNavigable: true,
        filterParams: {
          values: filterMcaStatusValues,
          cellRenderer: ({ value }: ValueFormatterParams) => {
            return (
              <BootstrapTooltip title={value !== '(Select All)' ? getStatusTooltipTitle(value) : ''} placement="top">
                <Typography>{getStatusLabel(value)}</Typography>
              </BootstrapTooltip>
            );
          }
        }
      },
      {
        initialHide: true,
        headerClass: 'ag-header-cell-cash-advance-group-right',
        toolPanelClass: ['ag-header-cell-cash-advance-group-right'],
        headerName: 'Funding Date',
        field: 'fundingDate',
        filter: 'agDateColumnFilter',
        type: 'rightAligned',
        valueGetter: (params) => params.data ? getFormattedDateUTC(params.data.fundingDate) : null,
        filterParams: {
          defaultOption: 'inRange',
          suppressAndOrCondition: true,
          filterOptions: ['inRange']
        }
      },
      {
        headerClass: 'ag-header-transaction-cell-center',
        toolPanelClass: ['ag-header-transaction-cell-center'],
        headerName: 'Start Date',
        headerComponent: SortableIconHeader,
        headerComponentParams: {
          icon: <FlagOutlined style={{ fontSize: 20 }} />,
          tooltipTitle: 'Start Date'
        },
        sort: 'desc',
        field: 'startDate',
        filter: 'agDateColumnFilter',
        type: 'rightAligned',
        valueGetter: (params) => params.data ? getFormattedDateUTC(params.data.startDate) : null,
        filterParams: {
          defaultOption: 'inRange',
          suppressAndOrCondition: true,
          filterOptions: ['inRange']
        }
      },
      {
        headerClass: 'ag-header-cell-merchant-group',
        toolPanelClass: ['ag-header-cell-merchant-group'],
        headerName: 'Merchant',
        field: 'merchant.legalName',
        tooltipField: 'merchant.legalName',
        cellRenderer: ({ value, data, node }: ICellRendererParams) => {
          if (node.isRowPinned()) {
            return null;
          }
          if (
            data.recurrencies &&
            data.recurrencies.length &&
            data.recurrencies.filter((rec: Recurrency) => rec.state === RecurrencyState.Error).length > 0
          ) {
            return <span style={{ color: '#FF4528', fontWeight: 'bold' }}>{value}</span>;
          }
          return <span>{value}</span>;
        }
      },
      {
        suppressColumnsToolPanel: !canViewMcaBroker,
        hide: true,
        headerClass: 'ag-header-cell-merchant-group',
        toolPanelClass: ['ag-header-cell-merchant-group'],
        headerName: 'Broker',
        field: 'broker',
        valueGetter: (params) => params.data ? getUserProfileFullName(params.data.broker) : null,
        tooltipValueGetter: (params) => params.data ? getUserProfileFullName(params.data.broker) : null,
        cellRenderer: ({ data }: ICellRendererParams) => {
          const broker = data?.broker;
          if (!broker) return null;
          const company = broker.company;
          return company?.legalName ? (
            <CompaniesWidget company={company} useCompactViewStyle={!!useCompactView} />
          ) : (
            <UserCellRenderer
              data={broker}
              spacing={1.5}
              sx={{ pt: 0.25 }}
              showBadge={false}
              showRole={false}
              useCompactViewStyle={!!useCompactView}
            />
          );
        }
      },
      {
        suppressColumnsToolPanel: !canViewMcaISOManager,
        hide: true,
        headerClass: 'ag-header-cell-merchant-group',
        toolPanelClass: ['ag-header-cell-merchant-group'],
        headerName: 'ISO Manager',
        field: 'manager',
        tooltipValueGetter: (params) => params.data ? getUserProfileFullName(params.data.manager) : null,
        valueGetter: (params) => params.data ? getUserProfileFullName(params.data.manager) : null,
        cellRenderer: ({ data }: ICellRendererParams) => {
          const manager = data?.manager;
          if (!manager) return null;

          return (
            <UserCellRenderer
              data={manager}
              spacing={1.5}
              sx={{ pt: 0.25 }}
              showBadge={false}
              showRole={false}
              useCompactViewStyle={!!useCompactView}
            />
          );
        }
      },
      {
        suppressColumnsToolPanel: !canViewMcaLegal,
        hide: true,
        headerClass: 'ag-header-cell-merchant-group',
        toolPanelClass: ['ag-header-cell-merchant-group'],
        headerName: 'Legal',
        field: 'legal',
        tooltipValueGetter: (params) => params.data ? getUserProfileFullName(params.data.legal) : null,
        valueGetter: (params) => params.data ? getUserProfileFullName(params.data.legal) : null,
        cellRenderer: ({ data }: ICellRendererParams) =>
          data?.legal ? (
            <UserCellRenderer
              data={data.legal}
              spacing={1.5}
              sx={{ pt: 0.25 }}
              showBadge={false}
              showRole={false}
              useCompactViewStyle={!!useCompactView}
            />
          ) : null
      },
      {
        suppressColumnsToolPanel: !canViewMcaFundingCompany,
        hide: !canViewMcaFundingCompany,
        initialHide: true,
        headerClass: 'ag-header-cell-merchant-group-center',
        toolPanelClass: ['ag-header-cell-merchant-group-center'],
        headerName: 'Funding Company',
        headerComponent: SortableIconHeader,
        headerComponentParams: {
          icon: <BankOutlined style={{ fontSize: 20, color: 'slateblue' }} />,
          tooltipTitle: 'Funding Company'
        },
        field: 'company.legalName',
        cellClass: 'ag-centered-cell',
        tooltipField: 'company.legalName',
        cellRenderer: ({ data, node }: ICellRendererParams) => {
          if (node.isRowPinned()) {
            return null;
          }
          return <AssetLogo logo={data.company?.logo} name={data.company?.legalName} size={useCompactView ? 'xs' : 'sm'} />;
        },
        cellRendererParams: { useCompactViewStyle: !!useCompactView }
      },
      {
        suppressColumnsToolPanel: !canViewMcaFunder,
        hide: !canViewMcaFunder,
        headerClass: 'ag-header-cell-merchant-group',
        toolPanelClass: ['ag-header-cell-merchant-group'],
        headerName: 'Underwriter',
        field: 'funder.id',
        cellRenderer: ({ data, node }: ICellRendererParams) => {
          if (node.isRowPinned() || !data?.funder) {
            return null;
          }
          return <McaUnderwriterListWidget underwriter={data.funder} useCompactView={!!useCompactView} />;
        },
        tooltipValueGetter: (params) => {
          if (!params.data) return '';
          const names: string[] = [];
          if (params.data.funder?.name) names.push(params.data.funder.name);
          if (params.data.secondaryFunders?.length > 0) {
            params.data.secondaryFunders.forEach((sf: Underwriter) => {
              if (sf.name) names.push(sf.name);
            });
          }
          return names.join(', ');
        },
        filter: 'agSetColumnFilter',
        filterParams: {
          valueFormatter: (params: ValueFormatterParams) => {
            const rowFunder = params.data?.funder;
            if (rowFunder?.id === params.value && rowFunder?.name) {
              return rowFunder.name;
            }
            return params.value;
          }
        },
        valueGetter: (params) => {
          if (!params.data) return null;
          const funderIds = [];

          if (params.data.funder?.id) {
            funderIds.push(params.data.funder.id);
          }
          if (params.data.secondaryFunders?.length > 0) {
            params.data.secondaryFunders.forEach((sf: any) => {
              if (sf.id) funderIds.push(sf.id);
            });
          }

          return funderIds.length > 0 ? funderIds : null;
        }
      },
      {
        headerClass: 'ag-header-cell-cash-advance-group-right',
        toolPanelClass: ['ag-header-cell-cash-advance-group-right'],
        headerName: 'Expected End',
        field: 'endDate',
        initialHide: !isSuperAdmin,
        type: 'rightAligned',
        valueGetter: (params) => params.data ? getFormattedDateUTC(params.data.endDate) : null
      },
      {
        headerName: 'Funding',
        headerClass: 'ag-header-cell-cash-advance-group-right',
        toolPanelClass: ['ag-header-cell-cash-advance-group-right'],
        field: 'amount',
        cellRenderer: AgGridCurrencyCell,
        type: 'rightAligned'
      },
      {
        initialHide: isSuperAdmin,
        headerClass: 'ag-header-cell-cash-advance-group-right',
        toolPanelClass: ['ag-header-cell-cash-advance-group-right'],
        headerName: 'Factor',
        headerComponent: SortableIconHeader,
        headerComponentParams: {
          icon: <RiseOutlined style={{ fontSize: 20 }} />,
          tooltipTitle: 'Factor'
        },
        field: 'factorRate',
        cellRenderer: FactorCell,
        type: 'rightAligned'
      },
      {
        headerName: 'Payback',
        headerClass: 'ag-header-cell-cash-advance-group-right',
        toolPanelClass: ['ag-header-cell-cash-advance-group-right'],
        field: 'payback',
        cellRenderer: AgGridCurrencyCell,
        type: 'rightAligned'
      },
      {
        headerName: 'Payback w/ Fees',
        suppressColumnsToolPanel: !canViewMcaBalanceWithFees,
        hide: !canViewMcaBalanceWithFees,
        initialHide: true,
        headerClass: 'ag-header-cell-cash-advance-group-right',
        toolPanelClass: ['ag-header-cell-cash-advance-group-right'],
        field: 'paybackWFee',
        cellRenderer: AgGridCurrencyCell,
        type: 'rightAligned'
      },
      {
        initialHide: !isSuperAdmin,
        headerClass: 'ag-header-cell-cash-advance-group-right',
        toolPanelClass: ['ag-header-cell-cash-advance-group-right'],
        headerComponent: SortableIconHeader,
        headerComponentParams: {
          icon: <HistoryOutlined style={{ fontSize: 20 }} />,
          tooltipTitle: 'Term (D)'
        },
        headerName: 'Term (D)',
        field: 'term',
        type: 'rightAligned'
      },
      {
        initialHide: true,
        headerClass: 'ag-header-cell-cash-advance-group-right',
        toolPanelClass: ['ag-header-cell-cash-advance-group-right'],
        headerName: 'Term (Projected)',
        field: 'perfTerm',
        type: 'rightAligned'
      },
      {
        initialHide: true,
        headerClass: 'text-center-turquoise',
        toolPanelClass: ['ag-header-cell-cash-advance-group-right'],
        headerName: 'Pmt (D)',
        field: 'pmt',
        cellRenderer: AgGridCurrencyCell,
        type: 'rightAligned',
        headerComponent: () => {
          return (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <CustomTooltip title="Pmt (D)">
                <CreditCardOutlined style={{ fontSize: 20 }} />
              </CustomTooltip>
            </Box>
          );
        }
      },
      {
        suppressColumnsToolPanel: !canViewMcaCommission,
        hide: !canViewMcaCommission,
        initialHide: true,
        headerClass: 'ag-header-cell-cash-advance-group-right',
        toolPanelClass: ['ag-header-cell-cash-advance-group-right'],
        headerName: 'Commission %',
        wrapHeaderText: false,
        field: 'commissionPercentage',
        cellRenderer: AgGridPercentCell,
        type: 'rightAligned'
      },
      {
        suppressColumnsToolPanel: !canViewMcaCommission,
        hide: !canViewMcaCommission,
        initialHide: true,
        headerClass: 'ag-header-cell-cash-advance-group-right',
        toolPanelClass: ['ag-header-cell-cash-advance-group-right'],
        headerName: 'Commission',
        sortable: false,
        field: 'commissionAmount',
        cellRenderer: CommissionCell,
        valueGetter: (params) => {
          if (params.node?.isRowPinned()) {
            return (params.data?.commissionAmount || 0).toFixed(2);
          }
          const value = getCommissionAmount(params.data);
          return (typeof value === 'number' && !isNaN(value) ? value : 0).toFixed(2);
        },
        type: 'rightAligned'
      },
      {
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        headerName: 'Completion %',
        headerComponent: SortableIconHeader,
        headerComponentParams: {
          icon: <DashboardOutlined style={{ fontSize: 20 }} />,
          tooltipTitle: 'Completion %'
        },
        field: 'completion',
        cellRenderer: ProgressBarCell,
        cellRendererParams: (params: ICellRendererParams) => ({
          amtPaidClr: params.data?.amtpaidclr,
          amtPaidPnd: params.data?.amtpaidpnd,
          factorRate: params.data?.factorRate,
          completionValue: params.data?.completion,
          amount: params.data?.amount,
          useCompactViewStyle: !!useCompactView
        }),
        tooltipField: 'completion',
        tooltipComponent: McaProgressTooltip,
        tooltipComponentParams: (params: ICellRendererParams) => ({
          isPortfolio: false,
          amtPaidClr: params.data?.amtpaidclr,
          clearedBalance: params.data?.balanceclr
        })
      },
      {
        suppressColumnsToolPanel: !canViewMcaPnl,
        hide: !canViewMcaPnl,
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        headerName: 'P&L',
        sortable: false,
        field: 'pnlnet',
        cellRenderer: PnlNetCell,
        cellRendererParams: { useCompactViewStyle: !!useCompactView },
        valueGetter: (params) => {
          const value = getPnlNetValue(params.data, params.node?.isRowPinned() || false, currentUser);
          return (typeof value === 'number' && !isNaN(value) ? value : 0).toFixed(2);
        },
        type: 'rightAligned'
      },
      {
        hide: true,
        suppressColumnsToolPanel: true,
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        headerName: 'P&L Gross',
        sortable: false,
        field: 'pnlgross',
        cellRenderer: AgGridCurrencyChipCell,
        type: 'rightAligned'
      },
      {
        initialHide: true,
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        headerName: 'Amt Paid (Clr)',
        field: 'amtpaidclr',
        cellRenderer: AgGridCurrencyCell,
        type: 'rightAligned'
      },
      {
        initialHide: true,
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        headerName: 'Amt Paid (Pnd)',
        field: 'amtpaidpnd',
        cellRenderer: AgGridCurrencyCell,
        type: 'rightAligned'
      },
      {
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        headerName: 'Balance (Clr)',
        field: 'balanceclr',
        cellRenderer: AgGridCurrencyCell,
        type: 'rightAligned'
      },
      {
        initialHide: true,
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        headerName: 'Balance (Pnd)',
        field: 'balancePnd',
        cellRenderer: AgGridCurrencyCell,
        valueGetter: (params) => params.data?.balancePnd || 0,
        type: 'rightAligned'
      },
      {
        hide: true,
        suppressColumnsToolPanel: !canViewMcaFees,
        headerClass: 'ag-header-fees-group-right',
        toolPanelClass: ['ag-header-fees-group-right'],
        headerName: 'Fees (Clr)',
        field: 'totalclrfines',
        cellRenderer: AgGridCurrencyCell,
        type: 'rightAligned'
      },
      {
        hide: true,
        suppressColumnsToolPanel: !canViewMcaFees,
        headerClass: 'ag-header-fees-group-right',
        toolPanelClass: ['ag-header-fees-group-right'],
        headerName: 'Fees (Pnd)',
        field: 'totalfines',
        cellRenderer: AgGridCurrencyCell,
        type: 'rightAligned'
      },
      {
        suppressColumnsToolPanel: !canViewMcaBalanceWithFees,
        hide: !canViewMcaBalanceWithFees,
        initialHide: true,
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        headerName: 'Bal w/Fees (Clr)',
        field: 'balanceClrWFees',
        cellRenderer: AgGridCurrencyCell,
        type: 'rightAligned'
      },
      {
        initialHide: true,
        suppressColumnsToolPanel: !canViewMcaBalanceWithFees,
        hide: !canViewMcaBalanceWithFees,
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        headerName: 'Bal w/Fees (Pnd)',
        field: 'balancePndWFees',
        cellRenderer: AgGridCurrencyCell,
        valueGetter: (params) => params.data?.balancePndWFees || 0,
        type: 'rightAligned'
      },
      {
        initialHide: true,
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        headerName: 'Returns',
        field: 'nrt',
        type: 'rightAligned',
        valueFormatter: ({ value }) => {
          return value == null || value === '' || value === 0 ? '–' : value;
        }
      },
      {
        hide: true,
        suppressColumnsToolPanel: !canViewMcaFundingFees,
        headerClass: 'ag-header-fees-group-right',
        toolPanelClass: ['ag-header-fees-group-right'],
        headerName: 'Funding Fees',
        field: 'totalfees',
        cellRenderer: AgGridCurrencyCell,
        type: 'rightAligned'
      },
      {
        initialHide: true,
        headerClass: 'ag-header-cell-cash-advance-group-right',
        toolPanelClass: ['ag-header-cell-cash-advance-group-right'],
        headerName: 'Refi',
        field: 'payoff',
        cellRenderer: AgGridCurrencyCell,
        type: 'rightAligned',
        cellRendererParams: { showEmptyAsDash: true }
      },
      {
        hide: !canViewMcaNetWire,
        suppressColumnsToolPanel: !canViewMcaNetWire,
        initialHide: true,
        headerClass: 'ag-header-cell-cash-advance-group-right',
        toolPanelClass: ['ag-header-cell-cash-advance-group-right'],
        headerName: 'Net Wire',
        field: 'netWire',
        cellRenderer: AgGridCurrencyCell,
        type: 'rightAligned'
      },
      {
        suppressColumnsToolPanel: !canViewMcaIntegration,
        hide: !canViewMcaIntegration,
        headerName: 'Platform',
        headerClass: 'ag-header-transaction-cell-center',
        toolPanelClass: ['ag-header-transaction-cell-center'],
        headerComponent: SortableIconHeader,
        headerComponentParams: {
          icon: <InteractionOutlined style={{ fontSize: 20 }} />,
          tooltipTitle: 'Platform'
        },
        sortable: true,
        field: 'integration',
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        cellClass: 'ag-centered-cell',
        cellRenderer: ({ data, node, useCompactViewStyle }: ICellRendererParams & { useCompactViewStyle?: boolean }) => {
          if (node.isRowPinned()) {
            return null;
          }
          return (
            <ExternalConnectionCompanyLogo mcaData={data} size={useCompactViewStyle ? 'xs' : 'sm'} mt={useCompactView ? 0 : undefined} />
          );
        },
        cellRendererParams: { useCompactViewStyle: !!useCompactView }
      },
      {
        field: 'actions',
        headerName: '',
        hide: true,
        initialHide: true,
        cellRenderer: IconActionBtn,
        sortable: false,
        enablePivot: false,
        pivot: false,
        resizable: false,
        suppressNavigable: true,
        suppressHeaderMenuButton: true,
        aggFunc: null,
        pinned: 'right',
        suppressColumnsToolPanel: true,
        cellClass: 'no-border',
        cellRendererParams: {
          canViewMcaDetails: canViewMcaDetails
        }
      },
      {
        field: 'investor',
        hide: true,
        filter: 'agTextColumnFilter',
        lockVisible: true,
        initialHide: true,
        suppressNavigable: true,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true
      },
      {
        field: 'search',
        hide: true,
        aggFunc: null,
        enablePivot: false,
        pivot: false,
        lockVisible: true,
        initialHide: true,
        suppressNavigable: true,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          newRowsAction: 'keep'
        }
      }
      /*   ]
    }*/
    ],
    [useCompactView, mcaDisplayAttributeRows]
  );

  const [fetchData] = useMerchantCashAdvancesPreAggregatedLazyQuery({
    fetchPolicy: 'no-cache'
  });

  const [fetchSummaryData] = useMerchantCashAdvancesSummaryLazyQuery({
    fetchPolicy: 'no-cache'
  });
  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    event.returnValue = true;
  };
  const navElement = document.querySelector('nav');
  const handleLinkClick = (event: Event) => {
    if (event instanceof MouseEvent && openDrawer) {
      !window.confirm('The form will close and changes will be lost. Are you sure you want to proceed?') && event.preventDefault();
    }
  };

  useEffect(() => {
    if (openDrawer) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      navElement?.addEventListener('click', handleLinkClick);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        navElement?.removeEventListener('click', handleLinkClick);
      };
    }
  }, [openDrawer]);

  const resetFilters = () => {
    setFundingDateFilter('');
    setStartDateFilter('');
    setStatusFilter([]);
    setFunderFilter([]);
    if (gridApiRef.current) {
      gridApiRef.current.setFilterModel({
        status: null,
        fundingDate: null,
        startDate: null,
        'funder.id': null
      });
      gridApiRef.current.onFilterChanged();
    }
    setIsFilterApplied(false);
  };

  const onFilterChanged = useCallback(() => {
    if (!gridApiRef.current) return;
    const filterModel = gridApiRef.current.getFilterModel();
    setFilterState(filterModel);
  }, []);

  const onGridReady = useCallback(
    ({ api }: GridReadyEvent) => {
      applyGridColumnState(api, keyName);

      // Apply filters in priority order:
      // 1. Context filterState
      // 2. URL params
      // 3. localStorage filters
      let filterModel: any = null;

      if (filterState) {
        filterModel = filterState;
      } else if (startDateFilter || statusFilter || fundingDateFilter || funderFilter.length > 0) {
        filterModel = {} as any;

        if (startDateFilter) {
          filterModel.startDate = {
            filterType: 'date',
            type: 'inRange',
            dateFrom: dayjs(startDateFilter).utc().format(),
            dateTo: dayjs(startDateFilter).utc().endOf('month').format()
          };
        }
        if (fundingDateFilter) {
          filterModel.fundingDate = {
            filterType: 'date',
            type: 'inRange',
            dateFrom: dayjs(fundingDateFilter).utc().format(),
            dateTo: dayjs(fundingDateFilter).utc().endOf('month').format()
          };

        }

        if (statusFilter.length > 0) {
          filterModel.status = {
            values: Array.isArray(statusFilter) ? statusFilter : [statusFilter],
            filterType: 'set'
          };
        }

        if (funderFilter.length > 0) {
          filterModel['funder.id'] = {
            values: funderFilter,
            filterType: 'set'
          };
        }
      } else {
        const savedFilters = localStorage.getItem(keyName + '_FILTER_STATE');
        if (savedFilters) {
          filterModel = JSON.parse(savedFilters);
        }
      }

      if (filterModel) {
        api.setFilterModel(filterModel);
        setFilterState(filterModel);

        if (filterModel.search?.filter) {
          setSearchValue(filterModel.search.filter);
        }
      }

      if (filterModel?.investor?.filter) {
        setInitialInvestorId(filterModel.investor.filter);
      }

      if (filterModel?.['funder.id']?.values) {
        setFunderFilter(filterModel['funder.id'].values);
      }

      // Add filter changed listener
      api.addEventListener('filterChanged', onFilterChanged);
      gridApiRef.current = api;
      api.updateGridOptions({ serverSideDatasource });
    },
    [filterState, fundingDateFilter, startDateFilter, statusFilter, keyName]
  );
  const onGridPreDestroyed = useCallback(() => {
    gridApiRef.current = undefined;
  }, []);

  useAgGridOutsideClick(gridApiRef.current, true, 'mca-list-grid-container');

  const getRowId = useMemo<GetRowIdFunc>(() => {
    return (params: GetRowIdParams) => {
      return params.data.id || '';
    };
  }, []);

  const countExpandedRows = () => {
    if (gridApiRef.current === null) return;
    let expandedCount = 0;

    gridApiRef?.current?.forEachNode((node) => {
      if (node.expanded) {
        expandedCount++;
      }
    });

    setExpandedRows(expandedCount);
  };

  const onRowClicked = useCallback((event: RowClickedEvent) => {
    // Get the event path (different browsers might expose it differently)
    const eventPath = (event as any).eventPath || (event as any).composedPath?.();

    // Check if the first element in the path is a MuiChip (status cell)
    if (eventPath && eventPath[0]?.classList?.contains('MuiChip-label')) {
      // Skip expansion for status chip clicks
      return;
    }

    if (!isRowExpandable) {
      if (canViewMcaDetails) {
        navigate(`/merchants/mca/${event.node.data.id}`);
      }
      return;
    }
    const willExpand = !event.node.expanded;
    event.node.setExpanded(willExpand);
    setExpandedMcaId(willExpand ? event.node.data.id : null);
    countExpandedRows();
  }, []);

  const onSearchChange = (value: any) => {
    if (!gridApiRef.current) {
      return;
    }
    const fm = gridApiRef.current.getFilterModel();
    fm['search'] = {
      filterType: 'text',
      type: 'contains',
      filter: value
    };
    gridApiRef.current.setFilterModel(fm);
    gridApiRef.current.onFilterChanged();
  };

  const onPageSizeChanged = useCallback((params: PaginationChangedEvent) => {
    if (!params.api.paginationGetPageSize() || useScroll) {
      return;
    }
    localStorage.setItem(keyName + '_ROWS_PER_PAGE_STATE', params.api.paginationGetPageSize() + '');
  }, []);

  const refreshServerSide = useCallback(() => {
    if (!gridApiRef.current) {
      return;
    }
    gridApiRef.current.refreshServerSide();
  }, []);

  const refreshGrid = useCallback(() => {
    if (!gridApiRef.current) {
      return;
    }
    gridApiRef.current.refreshServerSide();
    setRefreshKey((prevKey) => prevKey + 1);
  }, []);



  const serverSideDatasource: IServerSideDatasource = {
    getRows: async (params: IServerSideGetRowsParams) => {
      if (params.api.isDestroyed()) {
        return;
      }

      try {
        const filterModel = (params.request.filterModel || {}) as any;
        const dateFrom = filterModel?.startDate?.dateFrom || null;
        const dateTo = filterModel?.startDate?.dateTo || null;
        const fundingDateFrom = filterModel?.fundingDate?.dateFrom || null;
        const fundingDateTo = filterModel?.fundingDate?.dateTo || null;
        const filters = filterModel || {};
        const hasStatusFilter = 'status' in filters && filters.status?.values?.length > 0;
        const hasDateFilter = 'startDate' in filters && filters.startDate?.dateFrom && filters.startDate?.dateTo;
        const hasFundingDateFilter = 'fundingDate' in filters && filters.fundingDate?.fundingDateFrom && filters.fundingDate?.fundingDateTo;
        const hasFunderFilter = 'funder.id' in filters && filters['funder.id']?.values?.length > 0;

        if (hasStatusFilter || hasDateFilter || hasFundingDateFilter || hasFunderFilter) setIsFilterApplied(true);

        const res = await fetchData({
          variables: {
            limit: (params.request.endRow || 0) - (params.request.startRow || 0),
            offset: params.request.startRow,
            status: filterModel?.status?.values,
            search: filterModel?.search?.filter,
            dateFrom,
            dateTo,
            fundingDateFrom,
            fundingDateTo,
            investorId: filterModel?.investor?.filter,
            funderIds: filterModel?.['funder.id']?.values,
            sortArgs: params.request.sortModel?.map((x) => ({ field: x.colId, direction: x.sort }) as SortArgs)
          }
        });

        const rows = res.data?.merchantCashAdvancesPreAggregated.data || [];
        const total = res.data?.merchantCashAdvancesPreAggregated.total || 0;
        setCurrentTotalRows(total);
        setCurrentPageRows(rows.length);

        // Render rows immediately
        if (useScroll) {
          params.success({ rowData: rows });
        } else {
          params.success({ rowData: rows, rowCount: total });
        }

        // Fetch summary asynchronously without blocking row rendering
        (async () => {
          try {
            const summaryRes = await fetchSummaryData({
              variables: {
                status: filterModel?.status?.values,
                search: filterModel?.search?.filter,
                dateFrom,
                dateTo,
                fundingDateFrom,
                fundingDateTo,
                investorId: filterModel?.investor?.filter
              }
            });

            if (params.api.isDestroyed()) {
              return;
            }

            const gridCurrentFilter = params.api.getFilterModel();
            const sameFilters = JSON.stringify(gridCurrentFilter) === JSON.stringify(filterModel || {});
            if (!sameFilters) {
              return;
            }

            params.api.setGridOption('pinnedBottomRowData', [
              {
                number: 'Totals',
                payback: summaryRes.data?.merchantCashAdvancesSummary.paybackSum || 0,
                paybackWFee: summaryRes.data?.merchantCashAdvancesSummary.paybackWFeeSum || 0,
                amtpaidclr: summaryRes.data?.merchantCashAdvancesSummary.amtPaidClrSum || 0,
                amtpaidpnd: summaryRes.data?.merchantCashAdvancesSummary.amtPaidPndSum || 0,
                balanceclr:
                  (summaryRes.data?.merchantCashAdvancesSummary.paybackSum || 0) -
                  (summaryRes.data?.merchantCashAdvancesSummary.amtPaidClrSum || 0),
                balanceClrWFees:
                  (summaryRes.data?.merchantCashAdvancesSummary.paybackSum || 0) -
                  (summaryRes.data?.merchantCashAdvancesSummary.amtPaidClrSum || 0) +
                  (summaryRes.data?.merchantCashAdvancesSummary.totalclrfines || 0),
                balancePnd:
                  (summaryRes.data?.merchantCashAdvancesSummary.paybackSum || 0) -
                  (summaryRes.data?.merchantCashAdvancesSummary.amtPaidPndSum || 0),
                balancePndWFees:
                  (summaryRes.data?.merchantCashAdvancesSummary.paybackSum || 0) -
                  (summaryRes.data?.merchantCashAdvancesSummary.amtPaidClrSum || 0) +
                  (summaryRes.data?.merchantCashAdvancesSummary.totalPendingPostFundingFeesSum || 0),
                amount: summaryRes.data?.merchantCashAdvancesSummary.amountSum || 0,
                factorRate: summaryRes.data?.merchantCashAdvancesSummary.factorRateAvg || 0,
                pmt: summaryRes.data?.merchantCashAdvancesSummary.pmtSum || 0,
                completion: summaryRes.data?.merchantCashAdvancesSummary.completionAvg || 0,
                pnlNet: summaryRes.data?.merchantCashAdvancesSummary.pnlnetSum || 0,
                pnlGross: summaryRes.data?.merchantCashAdvancesSummary.pnlgrossSum || 0,
                totalfees: summaryRes.data?.merchantCashAdvancesSummary.totalFeesSum || 0,
                totalclrfines: summaryRes.data?.merchantCashAdvancesSummary.totalclrfines || 0,
                totalfines:
                  (summaryRes.data?.merchantCashAdvancesSummary?.totalclrfines || 0) +
                  (summaryRes.data?.merchantCashAdvancesSummary?.totalPendingPostFundingFeesSum || 0),
                payoff: summaryRes.data?.merchantCashAdvancesSummary.payoffSum || 0,
                netWire: summaryRes.data?.merchantCashAdvancesSummary.netWireSum || 0,
                commissionAmount: summaryRes.data?.merchantCashAdvancesSummary.commissionSum || 0
              }
            ]);
          } catch (summaryError: any) {
            // summary fetch failed, ignore silently
          }
        })();

      } catch (error: any) {
        logger.error('MCALIST: datasource error', {
          message: error?.message,
          stack: error?.stack,
          request: params.request
        });
        params.fail();
      }
      applyGridColumnState(params.api, keyName);
    }
  };

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: 'agAggregationComponent',
          statusPanelParams: {
            aggFuncs: ['sum', 'min', 'max']
          }
        }
      ]
    };
  }, []);

  const pinnedBottomRowData = useMemo<any[]>(() => {
    return [
      {
        number: 'Totals',
        payback: 0,
        paybackWFee: 0,
        balanceclr: 0,
        balanceclrwfees: 0,
        balancepnd: 0,
        balancepndwfees: 0,
        amount: 0,
        factorRate: 0,
        pmt: 0,
        completion: 0,
        amtpaidclr: 0,
        amtpaidpnd: 0,
        pnlgross: 0,
        pnlnet: 0
      }
    ];
  }, []);

  const getRowClass = useCallback(
    (params: any) => {
      if (params.node.rowPinned === 'bottom') {
        return 'ag-pin-bottom-row-' + mode;
      }
    },
    [mode]
  );

  const detailCellRenderer = useMemo<any>(() => {
    return DetailCellRenderer;
  }, []);

  const { height, width } = useWindowDimensions();
  const [expandedRows, setExpandedRows] = useState(0);

  const gridHeight = useMemo<number>(() => {
    if (currentPageRows >= 1) {
      // Calculate the maximum allowable height based on the container height
      const maxGridHeight = height > MAX_HEIGHT ? height - HEIGHT_OFFSET : DEFAULT_HEIGHT;

      // Calculate height based on the number of rows
      let calculatedHeight = PADDING;
      calculatedHeight += currentPageRows * ROW_HEIGHT;
      calculatedHeight += expandedRows * rowHeightExpanded;
      // Return the smaller of the calculated height or maxGridHeight, but at least DEFAULT_HEIGHT
      return Math.max(Math.min(calculatedHeight, maxGridHeight), DEFAULT_HEIGHT);
    }
    // Default height if there are no rows
    return DEFAULT_HEIGHT;
  }, [height, currentPageRows, expandedRows, rowHeightExpanded]);

  const icons = useMemo<{
    [key: string]: Function | string;
  }>(() => {
    return {
      settings: '<span class="ag-icon ag-icon-settings"></span>'
    };
  }, []);

  const sideBar = useMemo<SideBarDef | string | string[] | boolean | null>(() => {
    return {
      position: 'right',
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: true,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true
          }
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
          toolPanelParams: {
            suppressFilterSearch: true
          }
        },
        {
          id: 'settings',
          labelDefault: 'Settings',
          labelKey: 'settings',
          iconKey: 'menu',
          toolPanel: McaListTools,
          toolPanelParams: {
            preferencesKey: GridType.McaGrid,
            displayCompactViewToggle: true,
            refreshGrid,
            keyName
          }
        }
      ]
    };
  }, [gridApiRef.current]);

  const handleInvestorChange = (selectedUser: Partial<User> | null) => {
    setInitialInvestorId(selectedUser?.id || null);
    if (gridApiRef.current) {
      const currentFilterModel = gridApiRef.current.getFilterModel();
      gridApiRef.current.setFilterModel({
        ...currentFilterModel,
        investor: { filterType: 'text', type: 'contains', filter: selectedUser?.id || '' }
      });
      gridApiRef.current.onFilterChanged();
    }
  };

  const handleExportReport = useCallback(async () => {
    setBackdropOpen(true);
    const fm = gridApiRef!.current!.getFilterModel() as any;
    const dateFrom = fm?.startDate?.dateFrom || null;
    const dateTo = fm?.startDate?.dateTo || null;
    const fundingDateFrom = fm?.fundingDate?.dateFrom || null;
    const fundingDateTo = fm?.fundingDate?.dateTo || null;
    const qp = {
      status: fm?.status?.values,
      search: fm?.search?.filter,
      dateFrom,
      dateTo,
      fundingDateFrom,
      fundingDateTo,
      investorId: fm?.investor?.filter
    };
    await reportApi.handleExportReport('merchant-cash-advances', qp);
    setBackdropOpen(false);
  }, []);

  const saveColumnsStateOnChange = useCallback(
    (event: ColumnResizedEvent | SortChangedEvent | ColumnMovedEvent | ColumnVisibleEvent) => {
      if (
        gridApiRef && event.source?.toLowerCase().includes('ui') &&
        ((event.type === 'columnResized' && event.finished) ||
          (event.type === 'columnMoved' && event.finished) ||
          event.type === 'sortChanged' ||
          event.type === 'columnVisible')
      ) {
        if (event.type === 'columnVisible') {
          const colId = event.column?.getColId();
          if (colId && event.column?.isVisible()) {
            gridApiRef.current?.autoSizeColumns([colId], true);
          }
        }
        const columnsState = JSON.stringify(gridApiRef.current?.getColumnState());
        localStorage.setItem(keyName + '_COLUMNS_STATE', columnsState);
      }
    },
    [gridApiRef]
  );

  return (
    <MainCard content={false}>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={backdropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <ScrollX>
        <Stack spacing={3}>
          <Stack
            direction={matchDownSM ? 'column' : 'row'}
            spacing={1}
            justifyContent="space-between"
            alignItems="center"
            sx={{ pb: 1.5, pt: 1.5, pr: 3, pl: 3 }}
          >
            <Stack direction={matchDownSM ? 'column' : 'row'} spacing={1} alignItems="center">
              <Typography variant="caption" color="secondary">
                Search
              </Typography>
              <OutlinedInput
                value={searchValue || ''}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  onSearchChange(e.target.value);
                }}
                placeholder={`Search ${currentTotalRows} records...`}
                startAdornment={<SearchOutlined />}
                size="small"
                sx={{ minWidth: '180px', pt: 0.125, pb: 0.125 }}
              />
              {isSuperAdmin && (
                <InvestorDropDownAutoComplete
                  investorFilterId={initialInvestorId || undefined}
                  onInvestorChange={handleInvestorChange}
                  rights={[
                    Rights.IsInvestor,
                    Rights.IsSuperAdmin,
                    Rights.IsBroker,
                    Rights.IsIsoManager,
                    Rights.IsFunder,
                    Rights.IsLegal,
                    Rights.IsMerchant
                  ]}
                />
              )}
              {isFilterApplied && (
                <Tooltip
                  title={<Chip label="Reset filters" variant="combined" color="primary" />}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        padding: 0
                      }
                    }
                  }}
                >
                  <IconButton onClick={resetFilters}>
                    <FilterOutlined />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Stack direction={matchDownSM ? 'column' : 'row'} alignItems="center" spacing={1}>
              {canCreateMca && (
                <CustomTooltip title="Create New">
                  <Button
                    variant="contained"
                    onClick={handleAdd}
                    disabled={!nonWorkingDays}
                    sx={{
                      fontSize: '12px',
                      minWidth: 'auto'
                    }}
                  >
                    <PlusOutlined />
                  </Button>
                </CustomTooltip>
              )}

              {canCreateMca && (
                <CustomTooltip title="Update All">
                  <Button
                    variant="contained"
                    disabled={reloadTransactions}
                    onClick={() => setReloadTransactions(true)}
                    sx={{
                      fontSize: '12px',
                      minWidth: 'auto'
                    }}
                  >
                    <ReloadOutlined />
                  </Button>
                </CustomTooltip>
              )}

              <CustomTooltip title="Export Report">
                <Button
                  variant="contained"
                  onClick={handleExportReport}
                  sx={{
                    fontSize: '12px',
                    minWidth: 'auto'
                  }}
                >
                  <ExportOutlined />
                </Button>
              </CustomTooltip>
            </Stack>
          </Stack>
        </Stack>
        <Box style={{ height: `${gridHeight}px` }} display="flex" flexDirection="column">
          <div
            id="mca-list-grid-container"
            className={`${mode === 'light' ? 'ag-theme-material' : 'ag-theme-material-dark'} ${useCompactView ? 'compact-view' : 'expanded-view'
              }`}
            style={gridStyle}
          >
            <AgGridReact<MerchantCashAdvance[]>
              suppressColumnVirtualisation={true}
              key={refreshKey}
              sideBar={sideBar}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              getRowId={getRowId}
              onGridReady={onGridReady}
              onGridPreDestroyed={onGridPreDestroyed}
              pagination={!useScroll}
              rowModelType={'serverSide'}
              asyncTransactionWaitMillis={75}
              cellSelection={true}
              statusBar={statusBar}
              tooltipShowDelay={100}
              tooltipHideDelay={5000}
              paginationPageSize={rowsPerPage}
              cacheBlockSize={CACHE_BLOCK_SIZE}
              maxBlocksInCache={CACHE_BLOCK_IN_CACHE}
              serverSideInitialRowCount={SERVER_SIDE_INITIAL_ROW_COUNT}
              pinnedBottomRowData={pinnedBottomRowData}
              masterDetail={true}
              detailRowAutoHeight={true}
              blockLoadDebounceMillis={BLOCK_LOAD_DEBOUNCE_MILLIS}
              onColumnResized={saveColumnsStateOnChange}
              onSortChanged={saveColumnsStateOnChange}
              onColumnMoved={saveColumnsStateOnChange}
              onColumnVisible={saveColumnsStateOnChange}
              detailCellRenderer={detailCellRenderer}
              rowSelection={{
                mode: 'multiRow',
                enableSelectionWithoutKeys: true,
                checkboxes: false,
                headerCheckbox: false
              }}
              suppressRowClickSelection={true}
              onRowClicked={onRowClicked}
              getRowClass={getRowClass}
              suppressCsvExport={true}
              suppressExcelExport={true}
              rowBuffer={ROW_BUFFER}
              cacheOverflowSize={CACHE_OVERFLOW_SIZE}
              maxConcurrentDatasourceRequests={MAX_CONCURRENT_REQUESTS}
              getContextMenuItems={() => ['copy']}
              paginationPageSizeSelector={[50, 100, 200, 500]}
              onPaginationChanged={onPageSizeChanged}
              suppressScrollOnNewData={true}
              suppressMaintainUnsortedOrder={true}
              purgeClosedRowNodes={true}
            />
          </div>
        </Box>
      </ScrollX>
      {/* entity is not passed by useState without openDrawer condition */}
      <Dialog
        maxWidth="xl"
        fullWidth
        onClose={() => {
          !cashAdvance ? setOpenDrawerConfirmation(true) : setOpenDrawer(false);
        }}
        open={openDrawer}
        sx={{ '& .MuiDialog-paper': { p: 0 }, zIndex: 100 }}
      >
        {openDrawer && (
          <AddMerchantCashAdvance
            open={openDrawer}
            nonWorkingDays={nonWorkingDays}
            handleDrawerOpen={() => {
              !cashAdvance ? setOpenDrawerConfirmation(true) : setOpenDrawer(false);
            }}
            applicationId={linkedApplication?.id || ''}
            entity={cashAdvance}
            onSave={(savedCashAdvance, openRecurrencyAchDetails) => {
              setOpenDrawer(false);
              if (openRecurrencyAchDetails) {
                setCashAdvance(savedCashAdvance);
                setOpenRecurrencyAchDetails(true);
              }
              refreshServerSide();
            }}
          />
        )}
      </Dialog>
      {reloadTransactions && (
        <IntegrationsImportProgress
          open={reloadTransactions}
          handleClose={() => {
            setReloadTransactions(false);
            refreshServerSide();
          }}
        />
      )}
      {cashAdvance && cashAdvance?.recurrencies && cashAdvance?.recurrencies?.length && (
        <RecurrencyAchDetailsDialog
          open={openRecurrencyAchDetails}
          recurrencyId={cashAdvance?.recurrencies && cashAdvance?.recurrencies.length ? cashAdvance?.recurrencies[0].id : ''}
          handleDone={() => setOpenRecurrencyAchDetails(false)}
        />
      )}
      {
        <ActionConfirmation
          title={'The form will close and changes will be lost. Are you sure you want to proceed?'}
          open={openDrawerConfirmation}
          useYesNoButtonLabels={true}
          type="error"
          handleClose={(approved: boolean) => {
            setOpenDrawerConfirmation(false);
            if (!approved) {
              return;
            }
            setOpenDrawer(false);
          }}
        />
      }
    </MainCard>
  );
};

export default McaList;
